import React, { useCallback, useEffect, useState } from 'react';
import { TrendingUp, Wallet, Info } from 'lucide-react';
import { paymentsApi } from '../api';
import { formatPrice } from '../store';
import { EmptyState } from '../components/ui/EmptyState';
import { SkeletonRows } from '../components/ui/Skeleton';
import { StatusBadge } from '../components/ui/StatusBadge';
import { useToast } from '../components/ui/ToastProvider';
import { Pagination, DEFAULT_PAGE_SIZE } from '../components/ui/Pagination';
import type { IncomeSummary, IncomeBucket, PaymentRow } from '../types';

type PeriodKey = 'today' | 'month' | 'all';
type KindFilter = '' | 'sale' | 'debt';

const PERIODS: { key: PeriodKey; label: string }[] = [
  { key: 'today', label: 'Bugun' },
  { key: 'month', label: 'Bu oy' },
  { key: 'all', label: 'Hammasi' },
];

const KINDS: { key: KindFilter; label: string }[] = [
  { key: '', label: 'Hammasi' },
  { key: 'sale', label: 'Sotuvdan' },
  { key: 'debt', label: "Qarz to'lovi" },
];

/** Bitta davr uchun karta: tushum + nimadan iborat ekani. */
const BucketCard: React.FC<{ title: string; bucket: IncomeBucket; showCost: boolean }> = ({
  title, bucket, showCost,
}) => {
  const methods = Object.entries(bucket.by_method).filter(([, v]) => Number(v.amount) > 0);

  return (
    <div className="card">
      <p className="text-xs sm:text-sm text-gray-500">{title}</p>
      <p className="text-2xl sm:text-3xl font-bold text-success-600 mt-1">
        {formatPrice(bucket.total)} so'm
      </p>
      <p className="text-xs text-gray-400 mt-0.5">{bucket.count} ta to'lov</p>

      <div className="mt-3 pt-3 border-t border-gray-100 space-y-1.5 text-sm">
        {Object.entries(bucket.by_kind).map(([key, v]) => (
          <div key={key} className="flex justify-between">
            <span className="text-gray-500">{v.label}</span>
            <span className="font-medium">{formatPrice(v.amount)} so'm</span>
          </div>
        ))}
      </div>

      {methods.length > 0 && (
        <div className="mt-2 pt-2 border-t border-gray-100 flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-400">
          {methods.map(([key, v]) => (
            <span key={key}>{v.label}: {formatPrice(v.amount)}</span>
          ))}
        </div>
      )}

      {showCost && bucket.cost_total !== undefined && (
        <div className="mt-3 pt-3 border-t border-gray-100 space-y-1.5 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500">Sotilgan tovar summasi</span>
            <span className="font-medium">{formatPrice(bucket.sold_total)} so'm</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Shundan tovar tannarxi</span>
            <span className="font-medium text-danger-600">
              −{formatPrice(bucket.cost_total)} so'm
            </span>
          </div>
          <div className="flex justify-between font-semibold">
            <span>Yalpi foyda</span>
            <span className={Number(bucket.profit) >= 0 ? 'text-success-600' : 'text-danger-600'}>
              {formatPrice(bucket.profit || 0)} so'm
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export const Income: React.FC = () => {
  const toast = useToast();
  const [summary, setSummary] = useState<IncomeSummary | null>(null);
  const [rows, setRows] = useState<PaymentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<PeriodKey>('today');
  const [kind, setKind] = useState<KindFilter>('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [denied, setDenied] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = { page, page_size: pageSize };
      if (kind) params.kind = kind;
      if (period !== 'all' && summary) {
        params.date_from = period === 'today' ? summary.date : summary.month_start;
      }

      const res = await paymentsApi.list(params);
      const raw = res.data as any;
      setRows(Array.isArray(raw) ? raw : raw.results || []);
      setTotal(Array.isArray(raw) ? raw.length : raw.count || 0);
    } catch (error: any) {
      if (error.response?.status === 404 && page > 1) {
        setPage(page - 1);
        return;
      }
      if (error.response?.status === 403) {
        setDenied(true);
        return;
      }
      console.error('Payments fetch error:', error);
      toast.error('Kirimlarni yuklashda xatolik');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, kind, period, summary]);

  useEffect(() => {
    paymentsApi.summary()
      .then((res) => setSummary(res.data))
      .catch((error) => {
        if (error.response?.status === 403) setDenied(true);
      });
  }, []);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  if (denied) {
    return (
      <div className="space-y-4">
        <h1 className="page-bar text-xl sm:text-2xl font-bold text-gray-900">Kirimlar</h1>
        <div className="card">
          <EmptyState
            icon={Wallet}
            title="Ruxsat yo'q"
            description="Kassa kirimlarini faqat do'kon admini ko'ra oladi."
          />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="page-bar">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Kirimlar</h1>
        <p className="text-sm text-gray-500 mt-1">
          Mijozlardan haqiqatda olingan pul — sotuv paytida to'langani va eski
          qarz uchun keyin kelgani
        </p>
      </div>

      {/* Nima hisoblanayotgani ochiq aytilsin — raqamning ma'nosi shu */}
      <div className="card bg-gray-50 flex gap-3 items-start">
        <Info size={18} className="text-gray-400 shrink-0 mt-0.5" />
        <p className="text-sm text-gray-600">
          Bu yerdagi summa — kassaga <span className="font-medium">kirgan pul</span>,
          foyda emas. Har bir so'm quyidagi ro'yxatdagi aniq to'lovdan yig'iladi:
          qaysi buyurtma, qaysi mijoz va qanday usulda to'langani ko'rinib turadi.
          {summary?.show_cost && ' Sotilgan tovarning tannarxi ayirilgan yalpi foyda ham quyida.'}
        </p>
      </div>

      {summary && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
          <BucketCard title="Bugun" bucket={summary.today} showCost={summary.show_cost} />
          <BucketCard title="Bu oy" bucket={summary.month} showCost={summary.show_cost} />
        </div>
      )}

      {/* Filtrlar */}
      <div className="card">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex gap-2">
            {PERIODS.map((p) => (
              <button
                key={p.key}
                onClick={() => { setPeriod(p.key); setPage(1); }}
                className={`btn ${period === p.key ? 'btn-primary' : 'btn-secondary'}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="flex gap-2 sm:ml-auto">
            {KINDS.map((k) => (
              <button
                key={k.key}
                onClick={() => { setKind(k.key); setPage(1); }}
                className={`btn ${kind === k.key ? 'btn-primary' : 'btn-secondary'}`}
              >
                {k.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* To'lovlar ro'yxati — "pul qayerdan keldi" degan savolning javobi */}
      <div className="card overflow-hidden !p-0">
        {loading ? (
          <SkeletonRows />
        ) : rows.length === 0 ? (
          <EmptyState icon={TrendingUp} title="Kirim topilmadi" />
        ) : (
          <>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Vaqt</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Buyurtma</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Mijoz</th>
                    <th className="text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Turi</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Usul</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Summa</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 text-gray-500 whitespace-nowrap">
                        {new Date(row.created_at).toLocaleString('uz-UZ', {
                          day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
                        })}
                      </td>
                      <td className="py-3 px-4 font-mono text-sm">{row.order_number}</td>
                      <td className="py-3 px-4">{row.customer_name || "Noma'lum"}</td>
                      <td className="py-3 px-4">
                        <div className="flex justify-center">
                          <StatusBadge tone={row.kind === 'sale' ? 'success' : 'info'}>
                            {row.kind_display}
                          </StatusBadge>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-gray-500">{row.method_display}</td>
                      <td className="py-3 px-4 text-right font-semibold whitespace-nowrap">
                        {formatPrice(row.amount)} UZS
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobil ko'rinish */}
            <div className="md:hidden divide-y divide-gray-100">
              {rows.map((row) => (
                <div key={row.id} className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-medium text-gray-900 truncate">
                        {row.customer_name || "Noma'lum mijoz"}
                      </div>
                      <div className="text-xs text-gray-400 mt-0.5">
                        {row.order_number} • {new Date(row.created_at).toLocaleString('uz-UZ', {
                          day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
                        })}
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <StatusBadge tone={row.kind === 'sale' ? 'success' : 'info'}>
                          {row.kind_display}
                        </StatusBadge>
                        <span className="text-xs text-gray-400">{row.method_display}</span>
                      </div>
                    </div>
                    <span className="font-bold whitespace-nowrap">
                      {formatPrice(row.amount)} UZS
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {!loading && total > 0 && (
          <Pagination
            page={page}
            pageSize={pageSize}
            count={total}
            onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
          />
        )}
      </div>
    </div>
  );
};
